// src/pages/Upgrade.js
// Upgrade Page — Pricing with Visual Proof (PRD Website Conversion S3)

import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';
import SEOHead from '../components/common/SEOHead';
import { ScreenshotFrame, PublicHeader, PublicFooter } from '../components/common';
import { ENTERPRISE_SIZING_MAILTO } from '../utils/enterpriseContact';
import { track as trackTelemetry } from '../services/telemetryService';
import './Upgrade.css';

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

const LOOKUP_KEYS = {
  standard_monthly: 'standard_monthly',
  standard_yearly: 'standard_annual',
  pro_monthly: 'pro_monthly',
  pro_yearly: 'pro_annual',
};

function Upgrade() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [checkoutLoading, setCheckoutLoading] = useState(null); // null | 'standard' | 'pro'
  const [checkoutError, setCheckoutError] = useState(null);

  const isTrialExpired = location.state?.reason === 'trial_expired';

  // PRD-171A WP4b / P0-2: /upgrade is the page the nav labels "Pricing" and it
  // had no trial CTA at all -- the highest-intent public page, with the 30-day
  // trial mentioned only as FAQ prose. prompt:'create' sends cold visitors to
  // the CIAM sign-UP form rather than a sign-IN form they have no account for.
  const handleTrial = () => {
    if (process.env.NODE_ENV === 'development') {
      navigate('/command-center');
    } else {
      instance.loginRedirect({ ...loginRequest, prompt: 'create' });
    }
  };

  const handleSubscribe = async (tier) => {
    setCheckoutError(null);
    // PRD-151 WP2: upgrade CTA telemetry. `tier` is a fixed plan enum
    // ('standard' | 'pro') — never a domain/asset/user value.
    try { trackTelemetry('action', { meta: { feature: 'billing', action: 'upgrade_click', detail: tier } }); } catch (err) { /* best-effort */ }
    const key = `${tier}_${billingPeriod}`;
    const lookupKey = LOOKUP_KEYS[key];
    if (!lookupKey) {
      setCheckoutError('Invalid plan selection.');
      return;
    }

    if (!isAuthenticated || !accounts[0]) {
      // Save intended destination so the user lands back on /upgrade after login
      sessionStorage.setItem('postLoginRedirect', '/upgrade');
      instance.loginRedirect(loginRequest);
      return;
    }

    setCheckoutLoading(tier);
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0],
      });
      const idToken = tokenResponse.idToken;

      const response = await fetch(`${API_BASE_URL}/stripe-create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ lookupKey }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Checkout creation failed (${response.status}). ${text}`);
      }

      const data = await response.json();
      if (!data.url) {
        throw new Error('Stripe Checkout URL missing from response.');
      }

      window.location.href = data.url;
    } catch (err) {
      console.error('Subscribe error:', err);
      setCheckoutError(err.message || 'Could not start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "DriftAlarm",
    "description": "External Attack Surface Monitoring with AI Drift Detection",
    "brand": { "@type": "Organization", "name": "DriftAlarm" },
    "offers": [
      { "@type": "Offer", "price": "99", "priceCurrency": "USD", "name": "Standard", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock" },
      { "@type": "Offer", "price": "389", "priceCurrency": "USD", "name": "Pro", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock" }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Can I try before I buy?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! Every new account gets a free 30-day trial with full Standard-tier capabilities. No credit card required." } },
      { "@type": "Question", "name": "What happens when my trial expires?", "acceptedAnswer": { "@type": "Answer", "text": "Your data is preserved, but scanning pauses until you subscribe. You can pick up right where you left off." } },
      { "@type": "Question", "name": "How do I upgrade from Standard to Pro?", "acceptedAnswer": { "@type": "Answer", "text": "Change your plan yourself, any time, from Settings — switching between Standard and Pro is instant and self-serve, with prorated billing. Your data, baselines, and drift history carry over seamlessly." } },
      { "@type": "Question", "name": "What are Deep Scans?", "acceptedAnswer": { "@type": "Answer", "text": "Deep Scans go beyond fast reconnaissance — they include thorough vulnerability testing, SSL/TLS analysis, and nuclei-based detection templates for comprehensive coverage." } },
      { "@type": "Question", "name": "Why are all features available on every plan?", "acceptedAnswer": { "@type": "Answer", "text": "We believe security insights shouldn't be locked behind premium tiers. Whether you're on Standard or Enterprise, you get the same AI analysis, MITRE ATT&CK mapping, drift detection rules, and remediation guidance. The only difference between plans is how many assets you monitor and how often you scan." } }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Pricing" }
    ]
  };

  return (
    <div className="upgrade-container">
      <SEOHead
        title="Pricing | DriftAlarm Attack Surface Monitoring"
        description="DriftAlarm pricing — all features included at every tier. Standard from $99/mo for single domain, Pro for multi-domain monitoring. No feature gating."
        path="/upgrade"
        jsonLd={[productSchema, faqSchema, breadcrumbSchema]}
      />

      <PublicHeader activeLink="pricing" />

      {/* Main Content */}
      <div className="upgrade-content">
        {/* Expired Trial Banner */}
        {isTrialExpired && (
          <div className="trial-expired-banner">
            <div className="trial-expired-icon">!</div>
            <div className="trial-expired-text">
              <strong>Your 30-day trial has expired.</strong> Subscribe to a plan below to continue monitoring your attack surface.
            </div>
          </div>
        )}

        <div className="tier-selection">
          <h1 className="selection-title">Choose Your Plan</h1>
          <p className="selection-subtitle">
            All features included at every tier. Scale your monitoring as your attack surface grows.
            Every plan includes AI Security Briefs, MITRE ATT&CK Mapping, 41 Drift Detection Rules,
            Compliance Mapping, Vulnerability Validation, Prioritized Remediation, and Weekly Reports.
          </p>

          {checkoutError && (
            <div className="trial-expired-banner" role="alert" style={{ background: 'rgba(220, 38, 38, 0.1)' }}>
              <div className="trial-expired-icon">!</div>
              <div className="trial-expired-text">
                <strong>Couldn't start checkout.</strong> {checkoutError}
              </div>
            </div>
          )}

          {/* Trial CTA (PRD-171A P0-2). Only for signed-out visitors: an
              authenticated user -- especially one arriving here because their
              trial just expired -- must never be offered a free trial. */}
          {!isAuthenticated && (
            <div className="upgrade-trial-cta">
              <p className="upgrade-trial-cta__text">
                Not ready to pick a plan? Every new account gets a free 30-day trial
                with full Standard-tier capabilities. No credit card required.
              </p>
              <button
                type="button"
                className="da-btn da-btn--primary"
                onClick={handleTrial}
              >
                Start Free Trial
              </button>
            </div>
          )}

          {/* Billing Period Toggle */}
          <div className="billing-toggle">
            <button
              className={`billing-btn ${billingPeriod === 'yearly' ? 'active' : ''}`}
              onClick={() => setBillingPeriod('yearly')}
            >
              Yearly
            </button>
            <button
              className={`billing-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingPeriod('monthly')}
            >
              Monthly
            </button>
          </div>

          {/* Tier Cards */}
          <div className="tier-cards three-tier-layout">
            {/* Standard Tier */}
            <div className="tier-card standard">
              <div className="tier-header">
                <h3 className="tier-name">Standard</h3>
                <div className="tier-pricing">
                  <span className="tier-price">
                    {billingPeriod === 'yearly' ? '$999' : '$99'}
                  </span>
                  <span className="tier-period">
                    {billingPeriod === 'yearly' ? '/year' : '/month'}
                  </span>
                </div>
                {billingPeriod === 'yearly' && (
                  <span className="tier-savings">Save ~$189/year</span>
                )}
                <p className="tier-description">
                  Complete attack surface monitoring for a single domain and IP subnet.
                  AI-powered analysis, drift detection, and remediation guidance included.
                  Everything you need to know what attackers see.
                </p>
              </div>
              <ul className="tier-features">
                <li><span className="feature-check">&#10003;</span>1 monitored domain + 1 IP subnet</li>
                <li><span className="feature-check">&#10003;</span>10 Fast Scans per week</li>
                <li><span className="feature-check">&#10003;</span>5 Deep Scans per week</li>
                <li><span className="feature-check">&#10003;</span>3 scheduled scans</li>
                <li><span className="feature-check">&#10003;</span>1,000 URLs scanned per scan</li>
                <li><span className="feature-check">&#10003;</span>All AI analysis features included</li>
              </ul>
              <button
                type="button"
                className="da-btn da-btn--primary da-btn--block"
                onClick={() => handleSubscribe('standard')}
                disabled={checkoutLoading === 'standard'}
              >
                {checkoutLoading === 'standard' ? 'Starting checkout…' : 'Subscribe'}
              </button>
            </div>

            {/* Pro Tier */}
            <div className="tier-card pro">
              <div className="tier-header">
                <h3 className="tier-name">Pro</h3>
                <div className="tier-pricing">
                  <span className="tier-price">
                    {billingPeriod === 'yearly' ? '$3,899' : '$389'}
                  </span>
                  <span className="tier-period">
                    {billingPeriod === 'yearly' ? '/year' : '/month'}
                  </span>
                </div>
                {billingPeriod === 'yearly' && (
                  <span className="tier-savings">Save ~$769/year</span>
                )}
                <p className="tier-description">
                  5 domains and 10 subnets — monitor your entire external perimeter.
                  5x the scanning capacity with expanded URL coverage and the same
                  powerful AI analysis across every asset.
                </p>
              </div>
              <ul className="tier-features">
                <li><span className="feature-check">&#10003;</span>5 monitored domains + 10 subnets</li>
                <li><span className="feature-check">&#10003;</span>Cloud asset discovery</li>
                <li><span className="feature-check">&#10003;</span>50 Fast Scans per week</li>
                <li><span className="feature-check">&#10003;</span>25 Deep Scans per week</li>
                <li><span className="feature-check">&#10003;</span>15 scheduled scans</li>
                <li><span className="feature-check">&#10003;</span>2,000 URLs scanned per scan</li>
              </ul>
              <button
                type="button"
                className="da-btn da-btn--primary da-btn--block"
                onClick={() => handleSubscribe('pro')}
                disabled={checkoutLoading === 'pro'}
              >
                {checkoutLoading === 'pro' ? 'Starting checkout…' : 'Subscribe'}
              </button>
            </div>

            {/* Enterprise Tier */}
            <div className="tier-card team">
              <div className="tier-header">
                <h3 className="tier-name">Enterprise</h3>
                <div className="tier-pricing">
                  <span className="tier-price">Custom</span>
                  <span className="tier-period"></span>
                </div>
                <p className="tier-description">
                  Custom-scale monitoring for organizations with large or complex
                  external perimeters. Unlimited scanning, dedicated onboarding,
                  and volume pricing.
                </p>
              </div>
              <ul className="tier-features">
                <li><span className="feature-check">&#10003;</span>Custom domains &amp; IPs</li>
                <li><span className="feature-check">&#10003;</span>Unlimited Fast Scans</li>
                <li><span className="feature-check">&#10003;</span>Unlimited Deep Scans</li>
                <li><span className="feature-check">&#10003;</span>Unlimited schedules</li>
                <li><span className="feature-check">&#10003;</span>Volume pricing for large perimeters</li>
                <li><span className="feature-check">&#10003;</span>Custom URL scan limits</li>
              </ul>
              <a
                href={ENTERPRISE_SIZING_MAILTO}
                className="da-btn da-btn--secondary da-btn--block"
              >
                Get a Quote
              </a>
            </div>
          </div>

          {/* Visual Proof Strip */}
          <div className="visual-proof-strip">
            <h3 className="visual-proof-title">What You Get at Every Tier</h3>
            <div className="visual-proof-grid">
              <Link to="/how-it-works" className="visual-proof-item">
                <ScreenshotFrame
                  src="/images/screenshots/command-center"
                  alt="Security Command Center dashboard"
                  size="full"
                />
                <span className="visual-proof-label">Security Command Center</span>
              </Link>
              <Link to="/how-it-works" className="visual-proof-item">
                <ScreenshotFrame
                  src="/images/screenshots/da-brief"
                  alt="AI-generated threat analysis brief"
                  size="full"
                />
                <span className="visual-proof-label">AI Threat Analysis</span>
              </Link>
              <Link to="/how-it-works" className="visual-proof-item">
                <ScreenshotFrame
                  src="/images/screenshots/drift-alarm-rules"
                  alt="41 drift detection rules across 8 categories"
                  size="full"
                />
                <span className="visual-proof-label">41 Drift Detection Rules</span>
              </Link>
              <Link to="/how-it-works" className="visual-proof-item">
                <ScreenshotFrame
                  src="/images/screenshots/prioritized-recommendations"
                  alt="AI-ranked prioritized remediation recommendations"
                  size="full"
                />
                <span className="visual-proof-label">Prioritized Remediation</span>
              </Link>
            </div>
          </div>

          {/* Feature Comparison Matrix */}
          <div className="feature-comparison">
            <h3 className="comparison-title">Feature Comparison</h3>
            <div className="comparison-table-scroll">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th className="feature-col">Feature</th>
                    <th className="tier-col standard-col">Standard</th>
                    <th className="tier-col pro-col">Pro</th>
                    <th className="tier-col enterprise-col">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-row"><td colSpan="4">Scanning</td></tr>
                  <tr>
                    <td>Monitored assets</td>
                    <td>1 domain + 1 subnet</td>
                    <td>5 domains + 10 subnets</td>
                    <td>Custom</td>
                  </tr>
                  <tr>
                    <td>Cloud asset discovery</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>Fast Scans / week</td>
                    <td>10</td>
                    <td>50</td>
                    <td>Unlimited</td>
                  </tr>
                  <tr>
                    <td>Deep Scans / week</td>
                    <td>5</td>
                    <td>25</td>
                    <td>Unlimited</td>
                  </tr>
                  <tr>
                    <td>Scheduled scans</td>
                    <td>3</td>
                    <td>15</td>
                    <td>Unlimited</td>
                  </tr>
                  <tr>
                    <td>URLs scanned per asset</td>
                    <td>1,000 (prioritized)</td>
                    <td>2,000 (prioritized)</td>
                    <td>Custom</td>
                  </tr>

                  <tr className="section-row"><td colSpan="4">Detection &amp; Analysis</td></tr>
                  <tr>
                    <td>Subdomain enumeration</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>Port scanning</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>Vulnerability detection</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>SSL/TLS analysis</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>Drift detection &amp; baselines</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>AI security briefs</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>MITRE ATT&CK mapping</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>Compliance mapping (OWASP, CIS, NIST)</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>

                  <tr className="section-row"><td colSpan="4">Reporting</td></tr>
                  <tr>
                    <td>AI remediation guidance</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>PDF scan reports</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>PDF executive reports</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>Weekly trend reports</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>

                  <tr className="section-row"><td colSpan="4">Notifications</td></tr>
                  <tr>
                    <td>Drift alarm rules</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                  <tr>
                    <td>Email &amp; webhook alerts</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>

                  <tr className="section-row"><td colSpan="4">Support</td></tr>
                  <tr>
                    <td>Email support</td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                    <td><span className="cmp-check">&#10003;</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="upgrade-faq">
            <h3 className="comparison-title">Frequently Asked Questions</h3>
            <div className="faq-item">
              <h4>Can I try before I buy?</h4>
              <p>Yes! Every new account gets a free 30-day trial with full Standard-tier capabilities. No credit card required.</p>
            </div>
            <div className="faq-item">
              <h4>What happens when my trial expires?</h4>
              <p>Your data is preserved, but scanning pauses until you subscribe. You can pick up right where you left off.</p>
            </div>
            <div className="faq-item">
              <h4>How do I upgrade from Standard to Pro?</h4>
              <p>Change your plan yourself, any time, from Settings — switching between Standard and Pro is instant and self-serve, with prorated billing. Your data, baselines, and drift history carry over seamlessly.</p>
            </div>
            <div className="faq-item">
              <h4>What are Deep Scans?</h4>
              <p>Deep Scans go beyond fast reconnaissance — they include thorough vulnerability testing, SSL/TLS analysis, and nuclei-based detection templates for comprehensive coverage.</p>
            </div>
            <div className="faq-item">
              <h4>Why are all features available on every plan?</h4>
              <p>We believe security insights shouldn't be locked behind premium tiers. Whether you're on Standard or Enterprise, you get the same AI analysis, MITRE ATT&CK mapping, drift detection rules, and remediation guidance. The only difference between plans is how many assets you monitor and how often you scan.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer (unified) */}
      <PublicFooter />
    </div>
  );
}

export default Upgrade;
